import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Grid, Input, Select, Space, Spin, Tag, Typography } from "antd";
import { EnvironmentOutlined, SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import _ from "lodash";
import { geocodeCustomers } from "../../../../functions/geocoding";
import { formatAddress } from "../../../../functions/formatters";
import urls from "urls";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const { Option } = Select;
const { Text } = Typography;

const createCustomIcon = () =>
  new L.Icon({
    iconUrl: `/img/icons/pinpoint.svg`,
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    iconSize: [36, 40],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

const defaultIcon = createCustomIcon();
const highlightIcon = createCustomIcon("red");

const FlyToCustomer = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], 14, { duration: 1 });
    }
  }, [target, map]);
  return null;
};

const CustomerMap = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const allUsers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);

  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [countryFilter, setCountryFilter] = useState("NL");
  const [countries, setCountries] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState({ done: 0, total: 0 });
  const [flyTarget, setFlyTarget] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const markerRefs = useRef({});
  const geocodingStarted = useRef(false);

  useEffect(() => {
    if (!allUsers) return;

    const active = allUsers.filter((u) => u.authorization?.isAuthorized && !u.isSalesPerson && u.address?.delivery);

    const sorted = _.orderBy(active, (u) => u.company?.name?.toLowerCase(), ["asc"]);
    setCustomers(sorted);

    const uniqueCountries = _.uniq(sorted.map((u) => u.company?.country).filter(Boolean)).sort();
    setCountries(uniqueCountries);

    if (geocodingStarted.current) return;

    const needsGeocoding = sorted.filter(
      (c) => c.address?.delivery && (!c.address.delivery.lat || !c.address.delivery.lng),
    );

    if (needsGeocoding.length > 0) {
      geocodingStarted.current = true;
      setGeocoding(true);
      setGeocodingProgress({ done: 0, total: needsGeocoding.length });
      geocodeCustomers(needsGeocoding, (done, total) => {
        setGeocodingProgress({ done, total });
      }).finally(() => {
        setGeocoding(false);
      });
    }
  }, [allUsers]);

  const applyFilters = useCallback(
    (text, country) => {
      let result = customers;
      if (country) {
        result = result.filter((c) => c.company?.country === country);
      }
      if (text) {
        const lower = text.toLowerCase();
        result = result.filter(
          (c) =>
            c.company?.name?.toLowerCase().includes(lower) ||
            c.firstName?.toLowerCase().includes(lower) ||
            c.lastName?.toLowerCase().includes(lower) ||
            c.address?.delivery?.city?.toLowerCase().includes(lower),
        );
      }
      setFiltered(result);
    },
    [customers],
  );

  useEffect(() => {
    applyFilters(searchText, countryFilter);
  }, [customers, searchText, countryFilter, applyFilters]);

  const mappableCustomers = filtered.filter((c) => c.address?.delivery?.lat && c.address?.delivery?.lng);
  const withoutCoords = filtered.filter((c) => !c.address?.delivery?.lat || !c.address?.delivery?.lng);

  const centerNL = [52.2, 5.2];

  const handleFlyTo = (customer) => {
    const coords = { lat: customer.address.delivery.lat, lng: customer.address.delivery.lng };
    setFlyTarget(coords);
    setHighlightId(customer.id);
    setSelectedCustomer(customer);
    setTimeout(() => {
      const marker = markerRefs.current[customer.id];
      if (marker) marker.openPopup();
    }, 1200);
  };

  const sidebarWidth = isMobile ? "100%" : 300;

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "calc(100vh - 120px)", gap: 0 }}>
      <div
        style={{
          width: sidebarWidth,
          minWidth: isMobile ? undefined : 260,
          maxWidth: isMobile ? undefined : 340,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          borderRight: isMobile ? "none" : "1px solid #f0f0f0",
          borderBottom: isMobile ? "1px solid #f0f0f0" : "none",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 12px 8px 12px" }}>
          <Input
            placeholder={t("search")}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ marginBottom: 8 }}
          />
          <Select
            style={{ width: "100%" }}
            placeholder={t("country")}
            allowClear
            value={countryFilter}
            onChange={setCountryFilter}
          >
            {countries.map((c) => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Select>
          <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
            <Badge count={mappableCustomers.length} color="#1890ff" showZero overflowCount={9999} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("customers").toLowerCase()} op kaart
            </Text>
            {withoutCoords.length > 0 && (
              <Badge
                count={withoutCoords.length}
                color="orange"
                title={t("customer_map_no_address")}
                overflowCount={9999}
              />
            )}
          </div>
        </div>

        {geocoding && (
          <Alert
            style={{ margin: "0 12px 8px 12px", fontSize: 12 }}
            type="info"
            showIcon
            message={
              <span>
                <Spin size="small" style={{ marginRight: 6 }} />
                {t("customer_map_geocoding", { count: geocodingProgress.total - geocodingProgress.done })}
              </span>
            }
          />
        )}

        <div style={{ flex: 1, overflowY: "auto" }}>
          {!allUsers && (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spin />
            </div>
          )}
          {filtered.map((customer) => {
            const hasCoords = customer.address?.delivery?.lat && customer.address?.delivery?.lng;
            const isSelected = selectedCustomer?.id === customer.id;
            return (
              <div
                key={customer.id}
                onClick={() => hasCoords && handleFlyTo(customer)}
                style={{
                  padding: "10px 12px",
                  cursor: hasCoords ? "pointer" : "default",
                  borderBottom: "1px solid #f5f5f5",
                  background: isSelected ? "#e6f4ff" : "transparent",
                  transition: "background 0.2s",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {customer.company?.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {customer.address?.delivery?.city}
                      {customer.company?.country ? `, ${customer.company.country}` : ""}
                    </Text>
                  </div>
                  {!hasCoords && (
                    <Tag color="orange" style={{ fontSize: 11, marginLeft: 4 }}>
                      <EnvironmentOutlined /> ?
                    </Tag>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && allUsers && (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Text type="secondary">{t("customer_map_no_results")}</Text>
            </div>
          )}
        </div>

        <div style={{ padding: "8px 12px", borderTop: "1px solid #f0f0f0" }}>
          <Button
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => navigate(urls.customerList)}
            type="link"
            style={{ padding: 0 }}
          >
            {t("customer_map_open_list")}
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, position: "relative", minHeight: isMobile ? 400 : undefined }}>
        {!allUsers ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
            }}
          >
            <Space direction="vertical" align="center">
              <Spin size="large" />
              <Text type="secondary">{t("customer_map_loading")}</Text>
            </Space>
          </div>
        ) : (
          <MapContainer center={centerNL} zoom={8} style={{ height: "100%", width: "100%" }} zoomControl={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            <FlyToCustomer target={flyTarget} />
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={60}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();
                let size = 40;
                let className = "marker-cluster-small";
                if (count >= 10) {
                  size = 50;
                  className = "marker-cluster-medium";
                }
                if (count >= 50) {
                  size = 60;
                  className = "marker-cluster-large";
                }
                return L.divIcon({
                  html: `<div style="background: #938370; border-radius: 50%; width: ${size}px; height: ${size}px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${size > 40 ? 16 : 14}px; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">${count}</div>`,
                  className: className,
                  iconSize: L.point(size, size),
                });
              }}
            >
              {mappableCustomers.map((customer) => (
                <Marker
                  key={customer.id}
                  position={[customer.address.delivery.lat, customer.address.delivery.lng]}
                  icon={highlightId === customer.id ? highlightIcon : defaultIcon}
                  ref={(ref) => {
                    if (ref) markerRefs.current[customer.id] = ref;
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: 180 }}>
                      <strong style={{ display: "block", marginBottom: 4 }}>{customer.company?.name}</strong>
                      <span style={{ display: "block", fontSize: 12, color: "#666" }}>
                        {formatAddress(customer.address.delivery)}
                      </span>
                      <span style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                        {customer.firstName} {customer.lastName}
                      </span>
                      {customer.phoneNumber && (
                        <a
                          href={`tel:${customer.phoneNumber}`}
                          style={{ display: "block", fontSize: 12, marginTop: 2 }}
                        >
                          {customer.phoneNumber}
                        </a>
                      )}
                      {customer.email && (
                        <a href={`mailto:${customer.email}`} style={{ display: "block", fontSize: 12, marginTop: 2 }}>
                          {customer.email}
                        </a>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default CustomerMap;
